import * as React from "react";
import { Alert, Box, Container, Grid, Snackbar } from "@mui/material";

import MonitoringHeader from "../components/MonitoringHeader";
import DripSessionCreateForm from "../components/DripSessionCreateForm";
import DripSessionTable from "../components/DripSessionTable";

import {
  completeDripSession,
  getDripSessions,
  pauseDripSession,
  resumeDripSession,
  stopDripSession,
} from "../api/dripSessionsApi";
import { prettyError } from "../utils/prettyError";

export default function DripSessionsPage() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [toast, setToast] = React.useState({ open: false, type: "success", message: "" });
  const [actionLoadingId, setActionLoadingId] = React.useState(null);

  const fetchSessions = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = await getDripSessions();

      // supports DRF paginated and non-paginated responses
      if (Array.isArray(data)) {
        setRows(data);
      } else if (Array.isArray(data?.results)) {
        setRows(data.results);
      } else {
        setRows([]);
      }
    } catch (err) {
      setError(prettyError(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const showToast = (message, type = "success") => {
    setToast({ open: true, message, type });
  };

  const handleCreated = async () => {
    showToast("Drip session created successfully.", "success");
    fetchSessions();
  };

  const handlePause = async (row) => {
    try {
      setActionLoadingId(row.drip_session_id);
      await pauseDripSession(row.drip_session_id);
      showToast("Session paused successfully.", "success");
      fetchSessions();
    } catch (err) {
      showToast(prettyError(err), "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleResume = async (row) => {
    try {
      setActionLoadingId(row.drip_session_id);
      await resumeDripSession(row.drip_session_id);
      showToast("Session resumed successfully.", "success");
      fetchSessions();
    } catch (err) {
      showToast(prettyError(err), "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleStop = async (row) => {
    try {
      setActionLoadingId(row.drip_session_id);
      await stopDripSession(row.drip_session_id);
      showToast("Session stopped successfully.", "success");
      fetchSessions();
    } catch (err) {
      showToast(prettyError(err), "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleComplete = async (row) => {
    try {
      setActionLoadingId(row.drip_session_id);
      await completeDripSession(row.drip_session_id);
      showToast("Session completed successfully.", "success");
      fetchSessions();
    } catch (err) {
      showToast(prettyError(err), "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f7fafc",
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        <MonitoringHeader onRefresh={fetchSessions} loading={loading} />

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <DripSessionCreateForm onCreated={handleCreated} />
          </Grid>

          <Grid size={{ xs: 12, lg: 8 }}>
            <DripSessionTable
              rows={rows}
              loading={loading}
              error={error}
              onPause={handlePause}
              onResume={handleResume}
              onStop={handleStop}
              onComplete={handleComplete}
              actionLoadingId={actionLoadingId}
            />
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.type}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}